import React, { useCallback, useContext, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import { Tooltip } from "@mui/material";

import styles from "./Sidebar.module.css";
import { AuthContext } from "../../context/AuthContext";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { getSidebarRoutes } from "../../routes/routesConfig";

// Layout + animation constants (kept together to avoid magic numbers inline)
const TOOLTIP_SX = {
  backgroundColor: "#FFFFFF",
  color: "#5A6689",
  fontFamily: "Inter, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: 0,
  textAlign: "center",
  px: 1.75,
  py: 0.75,
  boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.16)",
  borderRadius: "8px",
};

const TOOLTIP_ARROW_SX = { color: "#FFFFFF" };

const MIN_SCALE = 1;
const MAX_SCALE = 1.2;
const MAX_TRANSLATE_X = 4;
const INFLUENCE_DISTANCE = 90;
const ITEM_HEIGHT = 48;
const ITEM_GAP = 4;
const PADDING_Y = 12;
const COLLAPSED_WIDTH = 67.03;
const EXPANDED_WIDTH = 248;
const SPRING_CONFIG = { stiffness: 360, damping: 32, mass: 0.9 };

const clamp01 = (value) => Math.max(0, Math.min(1, value));

function Sidebar({ isCollapsed }) {
  const location = useLocation();
  const { auth, logout } = useContext(AuthContext);
  const prefersReducedMotion = useReducedMotion();

  const sidebarItems = useMemo(() => {
    const routes = getSidebarRoutes();
    return routes.filter(
      (item) => item.roles.length === 0 || item.roles.includes(auth.user?.role)
    );
  }, [auth.user?.role]);

  const tooltipProps = useMemo(
    () => ({
      tooltip: { sx: TOOLTIP_SX },
      arrow: { sx: TOOLTIP_ARROW_SX },
    }),
    []
  );

  // Precompute center points for each item based on fixed sizing so we can keep
  // the math in motion values and avoid React re-renders on pointer move.
  const itemCenters = useMemo(
    () =>
      sidebarItems.map(
        (_, index) =>
          PADDING_Y + ITEM_HEIGHT / 2 + index * (ITEM_HEIGHT + ITEM_GAP)
      ),
    [sidebarItems]
  );

  // Stores Y position of the cursor relative to the dock (no React state).
  const mouseY = useMotionValue(Number.POSITIVE_INFINITY);
  const dockRef = useRef(null);

  const handleMouseMove = useCallback(
    (event) => {
      if (!isCollapsed || prefersReducedMotion) return;
      const bounds = dockRef.current?.getBoundingClientRect();
      if (!bounds) return;
      mouseY.set(event.clientY - bounds.top);
    },
    [isCollapsed, mouseY, prefersReducedMotion]
  );

  const handleMouseLeave = useCallback(() => {
    mouseY.set(Number.POSITIVE_INFINITY);
  }, [mouseY]);

  const isActivePath = useCallback(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
    [location.pathname]
  );

  const styleVars = useMemo(
    () => ({
      "--collapsed-width": `${COLLAPSED_WIDTH}px`,
      "--expanded-width": `${EXPANDED_WIDTH}px`,
      "--dock-item-size": `${ITEM_HEIGHT}px`,
      "--dock-gap": `${ITEM_GAP}px`,
      "--dock-padding-y": `${PADDING_Y}px`,
    }),
    []
  );

  return (
    <aside
      className={`${styles.sidebar} ${
        isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded
      }`}
      style={styleVars}
    >
      {isCollapsed ? (
        <motion.nav
          ref={dockRef}
          className={styles.dockTrack}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {sidebarItems.map((item, index) => (
            <DockItem
              key={item.path}
              item={item}
              centerY={itemCenters[index]}
              mouseY={mouseY}
              active={isActivePath(item.path)}
              prefersReducedMotion={prefersReducedMotion}
              tooltipProps={tooltipProps}
            />
          ))}
        </motion.nav>
      ) : (
        <nav className={styles.navList}>
          {sidebarItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
              }
              aria-label={item.alt}
            >
              <GlobalIcon
                iconName={item.iconName}
                className={styles.menuIcon}
                aria-label={item.alt}
              />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      )}

      {!isCollapsed && (
        <div className={styles.sidebarEnd}>
          <div className={styles.userBlock}>
            <div className={styles.username}>{auth.user?.username}</div>
            <div className={styles.logout} onClick={logout}>
              Logout
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

function DockItem({
  item,
  centerY,
  mouseY,
  active,
  prefersReducedMotion,
  tooltipProps,
}) {
  // Distance-based proximity drives the magnification transform.
  const proximity = useTransform(mouseY, (y) => {
    const distance = Math.abs(y - centerY);
    const clampedDistance = Math.min(distance, INFLUENCE_DISTANCE);
    const influence = 1 - clampedDistance / INFLUENCE_DISTANCE;
    return clamp01(influence);
  });

  // Spring for silky, dock-like easing.
  const proximitySpring = useSpring(proximity, SPRING_CONFIG);

  const scale = useTransform(proximitySpring, (p) =>
    prefersReducedMotion
      ? MIN_SCALE
      : MIN_SCALE + (MAX_SCALE - MIN_SCALE) * clamp01(p)
  );

  const translateX = useTransform(proximitySpring, (p) =>
    prefersReducedMotion ? 0 : MAX_TRANSLATE_X * clamp01(p)
  );

  return (
    <motion.div
      className={styles.dockItem}
      style={{ scale, translateX }}
      whileTap={{ scale: prefersReducedMotion ? 1 : 0.96 }}
    >
      <Tooltip
        title={item.label}
        placement="right"
        arrow
        disableInteractive
        componentsProps={tooltipProps}
      >
        <NavLink
          to={item.path}
          className={`${styles.dockLink} ${
            active ? styles.navLinkActive : ""
          }`}
          aria-label={item.alt}
        >
          <GlobalIcon
            iconName={item.iconName}
            className={styles.menuIcon}
            aria-label={item.alt}
          />
        </NavLink>
      </Tooltip>
    </motion.div>
  );
}

export default Sidebar;

